/**
 * ExploreCategoryGroup - Category group with subcategory links
 * Replicates the native app's ExploreCategoryGroup category type
 * Created by neo on 02.04.26
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Container } from 'reactstrap';
import { Link } from 'react-router-dom';
import { ExploreCategory, useExploreCategories } from '../../Model/Explore/ExploreCategory';
import { useLocalized } from '../../Hooks/useLocalized';
import { Theme } from '../../Theme/Theme';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const Section = styled.div<{ isLast?: boolean }>`
  padding: 48px 0;
  border-bottom: ${({ isLast }) => (isLast ? 'none' : '1px solid #f0f0f0')};
  animation: ${fadeInUp} 0.6s ease-out;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 800;
  color: ${Theme.Colors.textColor};
  margin: 0 0 20px 0;
  line-height: 1.2;
`;

const SubcategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SubcategoryLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: white;
  border-radius: 14px;
  text-decoration: none;
  color: inherit;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;

  &:hover {
    transform: translateX(4px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    color: inherit;
    text-decoration: none;
  }
`;

const SubcategoryName = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
`;

const Chevron = styled.span`
  font-size: 18px;
  color: ${Theme.Colors.warmGrey};
  flex-shrink: 0;
`;

const SkeletonItem = styled.div`
  height: 56px;
  border-radius: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

export type ExploreCategoryGroupProps = {
  category: ExploreCategory;
  isLast?: boolean;
  linkPrefix?: string;
};

export const ExploreCategoryGroup: React.FC<ExploreCategoryGroupProps> = observer(
  ({ category, isLast, linkPrefix = '/' }) => {
    const title = useLocalized(category.name);

    const { data: subCategories = [], isLoading } = useExploreCategories({
      parentId: category.id,
    });

    if (!isLoading && subCategories.length === 0) {
      return null;
    }

    return (
      <Section isLast={isLast}>
        <Container>
          <Title>{title}</Title>
          <SubcategoryList>
            {isLoading
              ? Array.from({ length: 3 }).map((_, i) => <SkeletonItem key={i} />)
              : subCategories.map((sub) => (
                  <SubcategoryEntry key={sub.id} subCategory={sub} linkPrefix={linkPrefix} />
                ))}
          </SubcategoryList>
        </Container>
      </Section>
    );
  },
);

type SubcategoryEntryProps = {
  subCategory: ExploreCategory;
  linkPrefix: string;
};

const SubcategoryEntry: React.FC<SubcategoryEntryProps> = observer(({ subCategory, linkPrefix }) => {
  const name = useLocalized(subCategory.name);
  const link = subCategory.link?.trim() || `${linkPrefix}${subCategory.identifier}`;

  return (
    <SubcategoryLink to={link}>
      <SubcategoryName>{name}</SubcategoryName>
      <Chevron>›</Chevron>
    </SubcategoryLink>
  );
});
